import React, { ChangeEvent, forwardRef, useContext, useEffect, useImperativeHandle, useState, useRef, useMemo, useCallback } from 'react';
import {
  Grid, TextField, Typography, MenuItem, Box, Select, SelectChangeEvent, useTheme,
  FormControlLabel, Switch,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocationResponse, StockCheckDetails, StockCheckItem, StockCheckLocation } from '../../../app/services/api/generated';
import { BarcodeScannerModal } from './BarcodeScanner/BarcodeScannerModal';
import OnlineOfflineSnackbar from './OnlineOfflineSnackbar';
import { StockCheckStatus } from '../../../enums/StockCheckStatus';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { UserContext } from '../../../components/shared/useUser';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useReordering } from '../contexts/ReorderingContext';
import SortableItemsTable from './SortableItemsTable';
import { Pill } from '../../../components/shared/Pill';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ZoneIcon } from './ZoneIcon';

interface StockCheckDetailsProps {
    stockCheckDetails: StockCheckDetails;
    isScannerModalOpen: boolean;
    zonesData: LocationResponse[];
    selectedZoneId: string;
    handleSelectedZoneChange: (event: SelectChangeEvent<string>) => void;
    allZonesEnabled: boolean;
    closeModal: () => void;
    handleSavingDataUserBackOnline: () => void;
    handleAutosaveMessage: (changesSaved: boolean) => void;
    handleCloseSaveAlert: () => void;
    showSavedAlert: boolean;
    onModifiedItemsChange: (modifiedItems: Set<number>) => void;
    onReorderingChange?: (isEnabled: boolean) => void;
    onSectionOrderChange?: (hasChanges?: boolean) => void;
    isBlockerActive: boolean;
}

export interface TempCategoryItem {
    name: string;
    items: StockCheckItem[];
    rowNumber?: number;
}

interface Section {
    id: string;
    type: 'location' | 'category';
    name: string;
    items: StockCheckItem[];
    rowNumber?: number;
    countZoneType?: string;
    countZoneIconKey?: string;
}

const StockCountDetails = forwardRef<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getUpdatedValues: () => { scheduledDate: string; stockCheckItems: StockCheckItem[]; orderChanged?: boolean; sectionsOrder?: any[]; itemsOrderData?: Array<{ locationId?: number; itemNumber?: string; order: number }> } | null;
    resetOrderChanged: () => void;
    notifyOrderChanged?: () => boolean;
      }, StockCheckDetailsProps>((props, ref) => {
        const {
          stockCheckDetails,
          isScannerModalOpen,
          closeModal,
          zonesData,
          selectedZoneId,
          handleSelectedZoneChange,
          allZonesEnabled,
          handleSavingDataUserBackOnline,
          handleAutosaveMessage,
          handleCloseSaveAlert,
          showSavedAlert,
          onModifiedItemsChange,
          onReorderingChange,
          onSectionOrderChange,
          isBlockerActive,
        } = props;

        const { t } = useTranslation('common');
        const theme = useTheme();
        const { logError } = useLogError();
        const { hasPermissionTo } = useContext(UserContext);
        const { saveStockCheckItem, saveStockCheckItemAtLocation } = useNswagClient();
        const {
          isReorderingEnabled: enableReorder,
          setReorderingEnabled: setEnableReorder,
          isItemReorderingEnabled: enableItemReorder,
          setItemReorderingEnabled: setEnableItemReorder,
          hasOrderChanged: orderChanged,
          setOrderChanged,
          hasItemOrderChanged: itemOrderChanged,
          setItemOrderChanged,
        } = useReordering();

        const stockCheckDetail = stockCheckDetails.stockCheck;
        const [scheduledDate, setScheduledDate] = useState<string>(
          stockCheckDetail?.plannedCountDate?.split('T')[0] ?? '',
        );
        const [stockCheckItems, setStockCheckItems] = useState<StockCheckItem[]>(
          stockCheckDetail?.items ?? [],
        );
        const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
        const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
        const [allSections, setAllSections] = useState<Section[]>([]);
        const [originalSections, setOriginalSections] = useState<Section[]>([]);
        const [itemsOrderData, setItemsOrderData] = useState<Array<{ locationId?: number; itemNumber?: string; order: number }>>([]);
        const completeOrderData: Array<{ locationId?: number; itemNumber: string | undefined; order: number; }> = [];

        const scrollContainerRef = useRef<HTMLDivElement>(null);
        const totalItemCount = stockCheckItems?.length ?? 0;
        const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

        useEffect(() => {
          if (modifiedItems.size > 0) {
            handleAutosaveMessage(false);
          }
        }, [modifiedItems]);

        // Helper to get counted items for a section (zone or category)
        const getSectionCountedItemCount = (section: Section) =>
          section.items?.filter(item =>
            item.itemValues?.some(val =>
              val.countQuantity !== undefined &&
                val.countQuantity !== null &&
                !isNaN(Number(val.countQuantity)) &&
                Number(val.countQuantity) !== 0,
            ),
          ).length ?? 0;

        // Process data and create sections when items or details change
        useEffect(() => {
          if (!stockCheckItems || !stockCheckDetail) return;

          const sections: Section[] = [];

          if (stockCheckDetail.locations?.length) {
            // Sort locations by rowNumber first, then countZoneType, then locationName
            const sortedLocations = [...stockCheckDetail.locations].sort((a, b) => {
              if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                const rowCompare = a.rowNumber - b.rowNumber;
                if (rowCompare !== 0) return rowCompare;
              } else if (a.rowNumber !== undefined) {
                return -1;
              } else if (b.rowNumber !== undefined) {
                return 1;
              }
              
              if (a.countZoneType && b.countZoneType) {
                const typeCompare = a.countZoneType.localeCompare(b.countZoneType);
                if (typeCompare !== 0) return typeCompare;
              } else if (a.countZoneType && !b.countZoneType) {
                return -1;
              } else if (!a.countZoneType && b.countZoneType) {
                return 1;
              }
              return (a.locationName || '').localeCompare(b.locationName || '');
            });

            sortedLocations.forEach((location) => {
              // Sort items by rowNumber within each location
              const locationItems = stockCheckItems
                .filter(item => item.locationId === location.locationId)
                .sort((a, b) => {
                  if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                    return a.rowNumber - b.rowNumber;
                  }
                  if (a.rowNumber !== undefined) return -1;
                  if (b.rowNumber !== undefined) return 1;
                  return (a.description || '').localeCompare(b.description || '');
                });

              if (locationItems.length > 0) {
                sections.push({
                  id: `loc-${location.locationId}`,
                  type: 'location',
                  name: location.locationName || '',
                  items: locationItems,
                  rowNumber: location.rowNumber,
                  countZoneType: location.countZoneType,
                  countZoneIconKey: location.countZoneIconKey,
                });
              }
            });
          }

          // Process items without location (or all items if no locations exist)
          const itemsWithoutLocation = stockCheckDetail.locations?.length ?
            getItemsWithoutLocation(stockCheckItems, stockCheckDetail.locations) :
            stockCheckItems;

          const categoriesByName = itemsGroupedByCategory(itemsWithoutLocation);

          categoriesByName.forEach((category) => {
            // Sort items by rowNumber within each category
            const sortedItems = [...category.items].sort((a, b) => {
              if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                return a.rowNumber - b.rowNumber;
              }
              if (a.rowNumber !== undefined) return -1;
              if (b.rowNumber !== undefined) return 1;
              return (a.description || '').localeCompare(b.description || '');
            });

            sections.push({
              id: `cat-${category.name}`,
              type: 'category',
              name: category.name,
              items: sortedItems,
              rowNumber: category.rowNumber,
            });
          });

          const sortedSections = [...sections].sort((a, b) => {
            if (a.type !== b.type) {
              return a.type === 'location' ? -1 : 1;
            }
            if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
              return a.rowNumber - b.rowNumber;
            }

            if (a.rowNumber !== undefined) return -1;
            if (b.rowNumber !== undefined) return 1;

            return a.name.localeCompare(b.name);
          });

          setAllSections(sortedSections);
        }, [stockCheckItems, stockCheckDetail]);

        useEffect(() => {
          setCollapsedSections({});
        }, [zonesData, selectedZoneId]);

        const handleToggleCollapse = (sectionId: string) => {
          setCollapsedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId],
          }));
        };

        const handleItemFocus = (itemId: number) => {
          if (lastFocusedItemId !== null &&
            lastFocusedItemId !== itemId &&
            modifiedItems.has(lastFocusedItemId)) {
            autosaveItem(lastFocusedItemId);
          }
          setLastFocusedItemId(itemId);
        };

        const autosaveItem = (itemId: number) => {
          const itemToSave = stockCheckItems.find(item => item.id === itemId);
          if (itemToSave) {
            handleItemAutosave(itemToSave);
          } else {
            handleAutosaveMessage(false);
          }
        };

        const handleItemAutosave = (itemToSave: StockCheckItem) => {
          const savePromise = selectedZoneId === '-1'
            ? saveStockCheckItem(stockCheckDetail?.id ?? 0, itemToSave)
            : saveStockCheckItemAtLocation(stockCheckDetail?.id ?? 0, parseInt(selectedZoneId), itemToSave);

          savePromise
            .then(() => {
              setModifiedItems(new Set());
              onModifiedItemsChange(new Set());
              handleAutosaveMessage(true);
            })
            .catch((err) => {
              logError(err);
            });
        };

        useImperativeHandle(
          ref,
          () => ({
            getUpdatedValues: () => {
              if (itemOrderChanged) {
                allSections.forEach(section => {
                  if (section.items && section.items.length > 0) {
                    // Get location ID if this is a location section
                    const locationId = section.type === 'location'
                      ? parseInt(section.id.replace('loc-', ''), 10)
                      : undefined;

                    // Process each item in this section in the order they appear in the UI
                    section.items.forEach((item, index) => {
                      completeOrderData.push({
                        locationId: item.locationId || locationId,
                        itemNumber: item.itemNumber,
                        order: index,
                      });
                    });
                  }
                });

                return {
                  scheduledDate,
                  stockCheckItems,
                  orderChanged,
                  sectionsOrder: allSections,
                  itemsOrderData: completeOrderData,
                };
              }

              // For other operations, return the standard data
              return {
                scheduledDate,
                stockCheckItems,
                orderChanged,
                sectionsOrder: allSections,
                itemsOrderData,
              };
            },
            resetOrderChanged: () => {
              setOrderChanged(false);
              setItemOrderChanged(false);
            },
            notifyOrderChanged: () => {
              return orderChanged || itemOrderChanged;
            },
          }),
          [scheduledDate, stockCheckItems, orderChanged, allSections, itemOrderChanged, itemsOrderData],
        );

        const handleScheduledDateChange = (event: ChangeEvent<HTMLInputElement>) => {
          setScheduledDate(event.target.value);
        };

        const handleQuantityChange = useCallback((
          itemId: number | undefined,
          valueId: number | undefined,
          newQuantityString: string,
        ) => {
          if (/^\d*\.?\d*$/.test(newQuantityString) || newQuantityString === '') {
            setStockCheckItems(prevItems => {
              // Only update the specific item that changed
              return prevItems.map(item => {
                if (item.id === itemId) {
                  return {
                    ...item,
                    itemValues: item.itemValues?.map(value => {
                      if (value.id === valueId) {
                        return {
                          ...value,
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          countQuantity: newQuantityString as any,
                        };
                      }
                      return value;
                    }),
                  };
                }
                return item; // Return unchanged item
              });
            });

            if (itemId !== undefined && !modifiedItems.has(itemId)) {
              setModifiedItems(prev => {
                const updatedItems = new Set(prev);
                updatedItems.add(itemId);
                onModifiedItemsChange(updatedItems);
                return updatedItems;
              });
            }

            handleCloseSaveAlert();
          }
        }, [modifiedItems, handleCloseSaveAlert, onModifiedItemsChange]);

        const handleQuantityBlur = useCallback((
          itemId: number | undefined,
          valueId: number | undefined,
          newQuantityString: string,
        ) => {
        // Skip processing if the leave page blocker modal is active
          if (isBlockerActive) {
            return;
          }

          const value = Number(newQuantityString);
          const newQuantity = isNaN(value) ? 0 : value;

          setStockCheckItems(prevItems => {
            return prevItems.map(item => {
              if (item.id === itemId) {
                return {
                  ...item,
                  itemValues: item.itemValues?.map(value => {
                    if (value.id === valueId) {
                      return {
                        ...value,
                        countQuantity: newQuantity,
                      };
                    }
                    return value;
                  }),
                };
              }
              return item;
            });
          });
        }, [isBlockerActive]);

        const handleEnableReorderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.checked;

          // If enabling reordering, save the current order as the original
          if (newValue && !enableReorder) {
            setOriginalSections([...allSections]);
          }

          // If disabling reordering and there are unsaved changes, restore the original order
          if (!newValue && enableReorder && orderChanged) {
            setAllSections(originalSections);
            setOrderChanged(false);
          }

          setEnableReorder(newValue);

          // Disable item reordering if location reordering is enabled
          if (newValue && enableItemReorder) {
            setEnableItemReorder(false);
          }

          if (onReorderingChange) {
            onReorderingChange(newValue);
          }
        };

        const handleEnableItemReorderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.checked;

          // Disable location reordering if item reordering is enabled
          if (newValue && enableReorder) {
            setEnableReorder(false);
          }

          setEnableItemReorder(newValue);

          // Reset item order data when disabling
          if (!newValue) {
            setItemOrderChanged(false);
            setItemsOrderData([]);
          }
        };

        const handleItemsReordered = (sectionId: string, newItems: StockCheckItem[]) => {
          const ROW_START = 10;
          const ROW_STEP = 10;

          const updatedItems = newItems.map((item, index) => ({
            ...item,
            rowNumber: ROW_START + (index * ROW_STEP),
          }));

          // Update the section with reordered items
          setAllSections(prevSections =>
            prevSections.map(section =>
              section.id === sectionId
                ? { ...section, items: updatedItems }
                : section,
            ),
          );

          // Update the main stockCheckItems 
          setStockCheckItems(prevItems => {
            const itemsMap = new Map(prevItems.map(item => [item.id, item]));

            updatedItems.forEach(item => {
              if (itemsMap.has(item.id)) {
                itemsMap.set(item.id, {
                  ...itemsMap.get(item.id),
                  rowNumber: item.rowNumber,
                });
              }
            });

            return Array.from(itemsMap.values());
          });

          let locationId: number | undefined;
          if (sectionId.startsWith('loc-')) {
            locationId = parseInt(sectionId.replace('loc-', ''), 10);
          }

          const orderData = updatedItems.map((item, idx) => ({
            locationId: item.locationId,
            itemNumber: item.itemNumber,
            order: idx, // idx is the correct order
          }));

          setItemsOrderData(orderData);

          setItemOrderChanged(true);
        };

        const onDragEnd = (result: DropResult) => {
          const { source, destination } = result;

          if (!destination || source.index === destination.index) return;

          if (source.droppableId === destination.droppableId) {
            const locationSections = allSections.filter(section => section.type === 'location');
            const categorySections = allSections.filter(section => section.type === 'category');

            if (source.droppableId === 'locations-sections') {
              const newLocationSections = [...locationSections];
              const [removed] = newLocationSections.splice(source.index, 1);
              newLocationSections.splice(destination.index, 0, removed);

              // Reorder collapsedSections to match newLocationSections order
              setAllSections([...newLocationSections, ...categorySections]);
              setCollapsedSections(prev => {
                const reordered: Record<string, boolean> = {};
                [...newLocationSections, ...categorySections].forEach(section => {
                  reordered[section.id] = prev[section.id] ?? false;
                });
                return reordered;
              });
            } else {
              const newCategorySections = [...categorySections];
              const [removed] = newCategorySections.splice(source.index, 1);
              newCategorySections.splice(destination.index, 0, removed);

              setAllSections([...locationSections, ...newCategorySections]);
              setCollapsedSections(prev => {
                const reordered: Record<string, boolean> = {};
                [...locationSections, ...newCategorySections].forEach(section => {
                  reordered[section.id] = prev[section.id] ?? false;
                });
                return reordered;
              });
            }

            setOrderChanged(true);

            if (onSectionOrderChange) {
              setTimeout(() => onSectionOrderChange(true), 0);
            }
          }
        };

        const itemsGroupedByCategory = useMemo(() => {
          return (items: StockCheckItem[]) => {
            const categories: { [key: string]: TempCategoryItem } = {};

            if (items) {
              for (const item of items) {
                if (item.category) {
                  const categoryKey = item.category.trim().toUpperCase().replace(/[\W]/gi, '');

                  if (!categories[categoryKey]) {
                    categories[categoryKey] = {
                      name: item.category,
                      items: [],
                    };
                  }
                }
              }
              for (const item of items) {
                if (item.category) {
                  const categoryKey = item.category.trim().toUpperCase().replace(/[\W]/gi, '');
                  if (categories[categoryKey]) {
                    categories[categoryKey].items.push(item);
                  }
                }
              }
            }

            return Object.values(categories).sort((a, b) => {
              if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                return a.rowNumber - b.rowNumber;
              }
              if (a.rowNumber !== undefined) return -1;
              if (b.rowNumber !== undefined) return 1;
              return a.name.localeCompare(b.name);
            });
          };
        }, []);

        const getItemsWithoutLocation = (items: StockCheckItem[], locations: StockCheckLocation[]) => {
          const locationIds = locations.map(loc => loc.locationId);
          return items.filter(item => !item.locationId || !locationIds.includes(item.locationId));
        };

        const renderLocationTable = (section: Section) => {
          const sortedItems = [...section.items].sort((a, b) => {
            if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
              return a.rowNumber - b.rowNumber;
            }
            if (a.rowNumber !== undefined) return -1;
            if (b.rowNumber !== undefined) return 1;
            return (a.description || '').localeCompare(b.description || '');
          });

          const counted = getSectionCountedItemCount(section);

          return (
            <Grid
              container
              key={section.id}
              className="stockcount-table-container"
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px',
                pb: '4px',
                marginTop: '16px',
                borderRadius: '12px',
                borderColor: theme.palette.custom.gray[300],
              }}
            >
              <Grid item
                className="stock-count-categories"
                xs={12}
                sx={{ mt: '0px' }}>
                <Box display="flex"
                  flexDirection="column"
                  my={4}
                  marginTop="0"
                  className="category">
                  <Box
                    display="flex"
                    flexDirection="row"
                    p={4}
                    className="title"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Left group: home icon, name, pill */}
                    <Box display="flex"
                      alignItems="center">
                      {/* Only show drag handle if reordering is enabled */}
                      {enableReorder && (
                        <DragIndicatorIcon
                          sx={{
                            color: theme.palette.custom.gray[500],
                            mr: 1,
                          }}
                        />
                      )}
                      <ZoneIcon 
                        iconName={section.countZoneIconKey} 
                        color={theme.palette.custom.gray[500]} 
                      />
                      <Typography
                        sx={{
                          mb: '4px',
                          ml: '8px',
                        }}
                      >
                        {t('countZone')}: {section.countZoneType ? <><em>{section.countZoneType}</em> - </> : ''}{section.name}
                      </Typography>
                      <Box sx={{ ml: 2 }}>
                        <Pill text={`${getSectionCountedItemCount(section)} / ${section.items.length} ${t('items')}`} />
                      </Box>
                    </Box>
                    {/* Always show collapse/expand icon */}
                    <Box
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        ml: 2,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleToggleCollapse(section.id);
                      }}
                    >
                      {collapsedSections[section.id]
                        ? <ChevronRightIcon sx={{ color: theme.palette.custom.gray[500] }} />
                        : <ExpandMoreIcon sx={{ color: theme.palette.custom.gray[500] }} />}
                    </Box>
                  </Box>
                  {/* Only show items table if not collapsed */}
                  {!collapsedSections[section.id] ? (
                    <Box sx={{ pl: 2, pr: 2 }}>
                      <SortableItemsTable
                        items={sortedItems}
                        onItemsReordered={(newItems) => handleItemsReordered(section.id, newItems)}
                        categoryName={section.name}
                        stockCheckDetail={stockCheckDetail}
                        stockCheckDetails={stockCheckDetails}
                        selectedZoneId={selectedZoneId}
                        allZonesEnabled={allZonesEnabled}
                        handleQuantityChange={handleQuantityChange}
                        handleQuantityBlur={handleQuantityBlur}
                        handleItemFocus={handleItemFocus}
                        modifiedItems={modifiedItems}
                        showSavedAlert={showSavedAlert}
                        sectionId={section.id}
                        onItemsOrderChanged={setItemOrderChanged}
                        key={section.id + '-items-table'}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Grid>
            </Grid>
          );
        };

        const renderCategoryTable = (category: TempCategoryItem) => {
        // Always sort by description in legacy mode
          const sortedItems = [...category.items].sort((a, b) =>
            (a.description || '').localeCompare(b.description || ''),
          );

          const counted = category.items?.filter(item =>
            item.itemValues?.some(val =>
              val.countQuantity !== undefined &&
                val.countQuantity !== null &&
                !isNaN(Number(val.countQuantity)) &&
                Number(val.countQuantity) !== 0,
            ),
          ).length ?? 0;

          return (
            <Grid
              container
              key={'cat-' + category.name}
              className="stockcount-table-container"
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px',
                pb: '4px',
                marginTop: '16px',
                borderRadius: '12px',
                borderColor: theme.palette.custom.gray[300],
              }}
            >
              <Grid item
                className="stock-count-categories"
                xs={12}
                sx={{ mt: '0px' }}>
                <Box key={category.name}
                  display="flex"
                  flexDirection="column"
                  my={4}
                  marginTop="0"
                  className="category">
                  <Box display="flex"
                    flexDirection="row"
                    p={4}
                    className="title">
                    <Typography sx={{ mb: '4px', ml: '8px' }}>{category.name}</Typography>
                    <Box sx={{ ml: 2 }}>
                      <Pill text={`${counted} / ${category.items.length} ${t('items')}`} />
                    </Box>
                  </Box>
                  <SortableItemsTable
                    items={sortedItems}
                    onItemsReordered={(newItems) => handleItemsReordered(`cat-${category.name}`, newItems)}
                    categoryName={category.name}
                    stockCheckDetail={stockCheckDetail}
                    stockCheckDetails={stockCheckDetails}
                    selectedZoneId={selectedZoneId}
                    allZonesEnabled={allZonesEnabled}
                    handleQuantityChange={handleQuantityChange}
                    handleQuantityBlur={handleQuantityBlur}
                    handleItemFocus={handleItemFocus}
                    modifiedItems={modifiedItems}
                    showSavedAlert={showSavedAlert}
                    sectionId={`cat-${category.name}`}
                    onItemsOrderChanged={setItemOrderChanged}
                    key={'cat-' + category.name + '-items-table'}
                  />
                </Box>
              </Grid>
            </Grid>
          );
        };

        const renderAllSections = () => {
        // If locations exist, show items directly for each location (no category grouping)
          if (stockCheckDetail?.locations?.length) {
            if (!enableReorder) {
              return (
                <div className="stock-count-zones-container">
                  {allSections.map(section => {
                    if (section.type === 'location') {
                      return renderLocationTable(section);
                    }
                    return null;
                  })}
                </div>
              );
            }

            // Reorderable draggable view for locations only
            const locationSections = allSections.filter(section => section.type === 'location');
            return (
              <DragDropContext onDragEnd={onDragEnd}>
                <div
                  ref={scrollContainerRef}
                  className="stock-count-zones-container"
                  style={{
                    height: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                    padding: '4px',
                    scrollBehavior: 'smooth',
                  }}
                >
                  <Droppable droppableId="locations-sections"
                    mode="standard">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          minHeight: locationSections.length ? '100px' : '0',
                          transition: 'background-color 0.2s ease',
                          background: snapshot.isDraggingOver ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                          borderRadius: '4px',
                        }}
                      >
                        {locationSections.map((section, index) => {
                          const isCollapsed = collapsedSections[section.id];
                          return (
                            <Draggable key={section.id}
                              draggableId={section.id}
                              index={index}>
                              {(providedDrag, dragSnapshot) => (
                                <div
                                  ref={providedDrag.innerRef}
                                  {...providedDrag.draggableProps}
                                  style={{
                                    ...providedDrag.draggableProps.style,
                                    marginBottom: '16px',
                                    minHeight: 80,
                                    transition: dragSnapshot.isDragging
                                      ? 'transform 0.18s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.18s cubic-bezier(0.2, 0, 0, 1)'
                                      : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.18s cubic-bezier(0.2, 0, 0, 1)',
                                    zIndex: dragSnapshot.isDragging ? 9999 : 'auto',
                                    transformOrigin: 'center',
                                    boxShadow: dragSnapshot.isDragging
                                      ? '0 8px 24px rgba(0,0,0,0.18), 0 3px 6px rgba(0,0,0,0.08)'
                                      : 'none',
                                    opacity: dragSnapshot.isDragging ? 0.97 : 1,
                                    willChange: 'transform, opacity, box-shadow',
                                  }}
                                >
                                  <Grid
                                    container
                                    className="stockcount-table-container"
                                    sx={{
                                      border: '1px solid',
                                      boxShadow: dragSnapshot.isDragging
                                        ? '0 10px 20px rgba(0, 0, 0, 0.15)'
                                        : '0 1px 2px 1px #1018280D',
                                      paddingTop: '8px',
                                      paddingBottom: '4px',
                                      borderRadius: '12px',
                                      borderColor: dragSnapshot.isDragging
                                        ? theme.palette.primary.main
                                        : theme.palette.custom.gray[300],
                                      backgroundColor: dragSnapshot.isDragging
                                        ? theme.palette.primary[50]
                                        : 'inherit',
                                      opacity: dragSnapshot.isDragging ? 0.92 : 1,
                                      transform: dragSnapshot.isDragging
                                        ? 'scale(1.01)'
                                        : 'scale(1)',
                                      transition: 'all 0.15s cubic-bezier(0.2, 0, 0, 1)',
                                      willChange: 'transform, opacity, box-shadow',
                                    }}
                                  >
                                    <Grid item
                                      xs={12}
                                      sx={{ mt: '0px' }}>
                                      <Box display="flex"
                                        flexDirection="column"
                                        my={4}
                                        marginTop="0"
                                        className="category">
                                        <Box
                                          display="flex"
                                          flexDirection="row"
                                          p={4}
                                          className="title"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          {...providedDrag.dragHandleProps}
                                          sx={{
                                            cursor: 'grab',
                                            '&:active': { cursor: 'grabbing' },
                                            userSelect: 'none',
                                          }}
                                        >
                                          {/* Left group: drag icon, home icon, name, pill */}
                                          <Box display="flex"
                                            alignItems="center">
                                            <DragIndicatorIcon
                                              sx={{
                                                color: dragSnapshot.isDragging
                                                  ? theme.palette.primary.main
                                                  : theme.palette.custom.gray[500],
                                                mr: 1,
                                                transform: dragSnapshot.isDragging ? 'rotate(-2deg)' : 'none',
                                                transition: 'transform 0.2s ease',
                                              }}
                                            />
                                            <ZoneIcon 
                                              iconName={section.countZoneIconKey}
                                              color={dragSnapshot.isDragging
                                                ? theme.palette.primary.main
                                                : theme.palette.custom.gray[500]}
                                            />
                                            <Typography
                                              sx={{
                                                mb: '4px',
                                                ml: '8px',
                                                fontWeight: dragSnapshot.isDragging ? 'bold' : 'normal',
                                                color: dragSnapshot.isDragging
                                                  ? theme.palette.primary.main
                                                  : 'inherit',
                                              }}
                                            >
                                              {t('countZone')}: {section.countZoneType ? <><em>{section.countZoneType}</em> - </> : ''}{section.name}
                                            </Typography>
                                            <Box sx={{ ml: 2 }}>
                                              <Pill text={`${getSectionCountedItemCount(section)} / ${section.items.length} items`} />
                                            </Box>
                                          </Box>
                                          {/* Right: expand/collapse icon */}
                                          <Box
                                            sx={{
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              ml: 2,
                                            }}
                                            onClick={e => {
                                              e.stopPropagation();
                                              handleToggleCollapse(section.id);
                                            }}
                                          >
                                            {isCollapsed
                                              ? <ChevronRightIcon sx={{
                                                color: dragSnapshot.isDragging
                                                  ? theme.palette.primary.main
                                                  : theme.palette.custom.gray[500],
                                              }} />
                                              : <ExpandMoreIcon sx={{
                                                color: dragSnapshot.isDragging
                                                  ? theme.palette.primary.main
                                                  : theme.palette.custom.gray[500],
                                              }} />}
                                          </Box>
                                        </Box>
                                        {/* Only show items table if not collapsed */}
                                        {!isCollapsed ? (
                                          <Box sx={{ pl: 2, pr: 2 }}>
                                            <SortableItemsTable
                                              items={[...section.items].sort((a, b) => {
                                                if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                                                  return a.rowNumber - b.rowNumber;
                                                }
                                                if (a.rowNumber !== undefined) return -1;
                                                if (b.rowNumber !== undefined) return 1;
                                                return (a.description || '').localeCompare(b.description || '');
                                              })}
                                              onItemsReordered={(newItems) => handleItemsReordered(section.id, newItems)}
                                              categoryName={section.name}
                                              stockCheckDetail={stockCheckDetail}
                                              stockCheckDetails={stockCheckDetails}
                                              selectedZoneId={selectedZoneId}
                                              allZonesEnabled={allZonesEnabled}
                                              handleQuantityChange={handleQuantityChange}
                                              handleQuantityBlur={handleQuantityBlur}
                                              handleItemFocus={handleItemFocus}
                                              modifiedItems={modifiedItems}
                                              showSavedAlert={showSavedAlert}
                                              sectionId={section.id}
                                              onItemsOrderChanged={setItemOrderChanged}
                                              key={section.id + '-items-table'}
                                            />
                                          </Box>
                                        ) : null}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </DragDropContext>
            );
          }

          // Legacy category grouping if no locations
          return (
            <div className="stock-count-zones-container">
              {allSections.map(section => {
                if (section.type === 'category') {
                  return (
                    <Box key={section.id + '-category-box'}
                      sx={{ mt: 2 }}>
                      {renderCategoryTable({ name: section.name, items: section.items })}
                    </Box>
                  );
                }
                return null;
              })}
            </div>
          );
        };

        return (
          <>
            <Grid container>
              {/* Created By */}
              <Grid
                item
                className="filter-pair-container"
                xs={12}
                sm={5}
                md={3}
                container
                spacing={2}
                sx={{ textAlign: 'left', ml: '0' }}
              >
                <Grid item
                  xs={12}>
                  <Typography variant="textLG">{t('createdBy')}</Typography>
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    value={stockCheckDetail?.createdBy}
                    disabled={true}
                    sx={{
                      width: '90%',
                      backgroundColor: theme.palette.custom.gray[200],
                    }}
                  />
                </Grid>
              </Grid>

              {/* Scheduled Date */}
              <Grid
                item
                className="filter-pair-container"
                xs={12}
                sm={5}
                md={3}
                container
                spacing={2}
                sx={{ textAlign: 'left', ml: '0' }}
              >
                <Grid item
                  xs={12}>
                  <Typography variant="textLG">{t('scheduledDate')}</Typography>
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    type="date"
                    required
                    value={scheduledDate}
                    onChange={handleScheduledDateChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: '90%',
                    }}
                  />
                </Grid>
              </Grid>
              {/* Zones Dropdown */}
              {zonesData && zonesData.length > 0 && (
                <Grid
                  item
                  className="filter-pair-container"
                  xs={12}
                  sm={5}
                  md={3}
                  container
                  spacing={2}
                  sx={{ textAlign: 'left', ml: '0' }}
                >
                  <Grid item
                    xs={12}>
                    <Typography variant="textLG">{t('stockCountZonesDdl')}</Typography>
                  </Grid>
                  <Grid item
                    xs={12}>
                    <Select
                      id="stock-count-zones-ddl"
                      sx={{
                        width: '90%',
                      }}
                      value={selectedZoneId}
                      onChange={handleSelectedZoneChange}
                    >
                      <MenuItem value="-1">
                        <em>{t('stockCountZonesDdlAll')}</em>
                      </MenuItem>
                      {zonesData.map((zone) => (
                        <MenuItem key={zone.id}
                          value={zone.id}>
                          {zone.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              )}

              {/* Reordering toggles */}
              {(stockCheckDetail?.status === StockCheckStatus.Pending ||
                    stockCheckDetail?.status === StockCheckStatus.Counting) &&
                    hasPermissionTo(['StockLocationWrite']) && (
                <>
                  {!!stockCheckDetail?.locations?.length && (
                    <Grid item
                      xs={12}
                      sm={5}
                      md={3}
                      container
                      spacing={1}
                      sx={{ textAlign: 'right', mt: '15px' }} > <Grid item
                        xs={12}
                        sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <FormControlLabel control={<Switch checked={enableReorder}
                          onChange={handleEnableReorderChange} />}
                        label={t('enableCountZonesSorting')}
                        labelPlacement="end" /> </Grid> <Grid item
                        xs={12}
                        sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <FormControlLabel control={<Switch checked={enableItemReorder}
                          onChange={handleEnableItemReorderChange} />}
                        label={t('enableItemsSorting')}
                        labelPlacement="end" /> </Grid> </Grid>
                  )}
                  <Grid item
                    xs={12}
                    sm={5}
                    md={3}
                    container
                    spacing={1}
                    sx={{ textAlign: 'right', mt: '15px' }}>
                    <Typography variant="textMD"
                      fontWeight={600}
                      sx={{ display: 'flex', alignItems: 'center' }}>
                      <Pill text={`${totalItemCount} ${t('totalItems')}`} />
                    </Typography>
                  </Grid>
                </>
              )}


              {/* Online/Offline Indicator */}
              {stockCheckDetail?.status === StockCheckStatus.Counting && (
                <Grid
                  item
                  xs={12}
                  md={zonesData?.length > 0 ? 2 : 5}
                  alignSelf={{ xs: 'center', md: 'end' }}
                  textAlign={{ xs: 'center', md: 'end' }}
                  sx={{ mt: '20px' }}
                >
                  <OnlineOfflineSnackbar onOnline={handleSavingDataUserBackOnline} />
                </Grid>
              )}
            </Grid>
            {/* Main Content - Sections List */}
            {renderAllSections()}

            {isScannerModalOpen ? <BarcodeScannerModal
              isScannerOpen={isScannerModalOpen}
              closeModal={closeModal}
              stockCheckItems={stockCheckItems}
              setStockCheckItems={setStockCheckItems}
              stockCountName={stockCheckDetail?.name}
            /> : null}
          </>
        );
      });

StockCountDetails.displayName = 'StockCountDetails';
export default StockCountDetails;